'use client';

import { useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { SearchVisualIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import { Alert } from '../../../components/alert';
import { Tabs } from '../../../components/tabs';
import { SettingsCard } from '../../settings/SettingsCard';
import { SettingsRow } from '../../settings/SettingsRow';
import { InternalNetworkEdrAssessmentModeDialog } from './InternalNetworkEdrAssessmentModeDialog';
import { InternalNetworkExecutionAutomationSection } from './InternalNetworkExecutionAutomationSection';
import { InternalNetworkExecutionTierDialog } from './InternalNetworkExecutionTierDialog';
import { InternalNetworkExecutionTierInfoBanner } from './InternalNetworkExecutionTierInfoBanner';
import {
  resolveInternalNetworkEdrAssessmentMode,
  type InternalNetworkExecutionPersistRequest,
} from './internal-execution.model';

const EXECUTION_SECTION = {
  profile: 'profile',
  automation: 'automation',
} as const;

type ExecutionSection =
  (typeof EXECUTION_SECTION)[keyof typeof EXECUTION_SECTION];

type EditDialog = 'executionTier' | 'edrAssessmentMode' | null;

const isExecutionSection = (value: string): value is ExecutionSection =>
  value === EXECUTION_SECTION.profile || value === EXECUTION_SECTION.automation;

export type InternalNetworkExecutionSettingsNetwork = Pick<
  InternalNetworkDto,
  | 'executionTier'
  | 'edrAssessmentMode'
  | 'schedule'
  | 'executionSchedule'
  | 'smokeTestEnabled'
>;

export type InternalNetworkExecutionSettingsContentProps = {
  network: InternalNetworkExecutionSettingsNetwork;
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
  isUpdating: boolean;
  onPersist: (
    request: InternalNetworkExecutionPersistRequest,
  ) => Promise<boolean>;
};

export function InternalNetworkExecutionSettingsContent({
  network,
  isReadOnly,
  isDiscoveryActive,
  isUpdating,
  onPersist,
}: InternalNetworkExecutionSettingsContentProps) {
  const t = useTranslations('designSystem.networkSettings.internalExecution');
  const tCommon = useTranslations('designSystem.common');
  const [activeTab, setActiveTab] = useState<ExecutionSection>(
    EXECUTION_SECTION.profile,
  );
  const [editDialog, setEditDialog] = useState<EditDialog>(null);
  const edrAssessmentMode = resolveInternalNetworkEdrAssessmentMode(
    network.edrAssessmentMode,
  );
  const settingsDisabled = isReadOnly || isDiscoveryActive;
  const editActionLabel = isDiscoveryActive
    ? t('discoveryLocked.disabledTooltip')
    : tCommon('edit');

  return (
    <>
      <VStack alignItems="stretch" gap={4} w="full">
        {isDiscoveryActive ? (
          <Alert.Root
            background="bg.warning.subtle"
            borderColor="border.warning.subtle"
          >
            <Alert.Icon asChild color="fg.warning.default">
              <SearchVisualIcon size={16} />
            </Alert.Icon>
            <Alert.Content>
              <Alert.Title>{t('discoveryLocked.title')}</Alert.Title>
              <Alert.Description>
                {t('discoveryLocked.description')}
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        ) : null}
        <Tabs.Root
          value={activeTab}
          onValueChange={({ value }) => {
            if (isExecutionSection(value)) {
              setActiveTab(value);
            }
          }}
          size="sm"
        >
          <Tabs.List>
            <Tabs.Trigger value={EXECUTION_SECTION.profile}>
              {t('profileSection')}
            </Tabs.Trigger>
            <Tabs.Trigger value={EXECUTION_SECTION.automation}>
              {t('automationSection')}
            </Tabs.Trigger>
            <Tabs.Indicator />
          </Tabs.List>

          <Tabs.Content value={EXECUTION_SECTION.profile}>
            <VStack alignItems="stretch" gap={6}>
              <InternalNetworkExecutionTierInfoBanner />
              <SettingsCard>
                <SettingsRow
                  label={t('executionTier')}
                  value={t(`executionTierValues.${network.executionTier}`)}
                  action={{
                    label: editActionLabel,
                    onClick: () => setEditDialog('executionTier'),
                    disabled: isDiscoveryActive,
                  }}
                  isReadOnly={isReadOnly}
                />
                <SettingsRow
                  label={t('edrAssessmentMode')}
                  value={t(`edrAssessmentModeValues.${edrAssessmentMode}`)}
                  action={{
                    label: editActionLabel,
                    onClick: () => setEditDialog('edrAssessmentMode'),
                    disabled: isDiscoveryActive,
                  }}
                  isReadOnly={isReadOnly}
                />
              </SettingsCard>
            </VStack>
          </Tabs.Content>

          <Tabs.Content value={EXECUTION_SECTION.automation}>
            <InternalNetworkExecutionAutomationSection
              network={network}
              disabled={settingsDisabled}
              isUpdating={isUpdating}
              onPersist={onPersist}
            />
          </Tabs.Content>
        </Tabs.Root>
      </VStack>

      <InternalNetworkExecutionTierDialog
        open={editDialog === 'executionTier'}
        onOpenChange={(open) => setEditDialog(open ? 'executionTier' : null)}
        executionTier={network.executionTier}
        isDiscoveryActive={isDiscoveryActive}
        isUpdating={isUpdating}
        onSubmit={(executionTier) =>
          onPersist({ updates: { executionTier }, showSuccessToast: true })
        }
      />

      <InternalNetworkEdrAssessmentModeDialog
        open={editDialog === 'edrAssessmentMode'}
        onOpenChange={(open) =>
          setEditDialog(open ? 'edrAssessmentMode' : null)
        }
        edrAssessmentMode={edrAssessmentMode}
        isDiscoveryActive={isDiscoveryActive}
        isUpdating={isUpdating}
        onSubmit={(mode) =>
          onPersist({
            updates: { edrAssessmentMode: mode },
            showSuccessToast: true,
          })
        }
      />
    </>
  );
}
